import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import {
    AnalyticsCapabilities,
    AnalyticsPreview,
    AnalyticsSchedulerStatus,
    AnalyticsSnapshot,
    AnalyticsSnapshotDto,
    SnapshotRevenue,
    UserService
} from '../../global';
import { ToastService } from '../../global/toast/toast';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs';

Chart.register(...registerables);

const CHART_COLORS = [
    'rgb(54, 162, 235)',
    'rgb(255, 99, 132)',
    'rgb(75, 192, 192)',
    'rgb(255, 159, 64)',
    'rgb(153, 102, 255)',
    'rgb(255, 205, 86)',
    'rgb(201, 203, 207)'
];

@Component({
    standalone: false,
    selector: 'abs-analytics',
    template: `
        <abs-page-layout [pageTitle]="'Analytics'">
            <div class="dashboard-grid">
                <!-- TAKE SNAPSHOT -->
                @if (canEdit) {
                    <div class="analytics-section">
                        <h2 class="section-title">Take Snapshot</h2>
                        <ion-list [formGroup]="snapshotForm">
                            @if (capabilities?.stripe) {
                                <ion-item>
                                    <ion-label>Stripe Monthly</ion-label>
                                    <div slot="end" class="auto-value">
                                        @if (stripeLoading) {
                                            <ion-spinner name="dots"></ion-spinner>
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else if (preview?.stripeMonthly != null) {
                                            <span>{{ preview?.stripeMonthly }}</span>
                                            @if (previewDeltas.stripeMonthly !== null && previewDeltas.stripeMonthly !== 0) {
                                                <span [class]="previewDeltas.stripeMonthly > 0 ? 'delta-up' : 'delta-down'"
                                                    >{{ previewDeltas.stripeMonthly > 0 ? '↑ +' : '↓ '
                                                    }}{{ previewDeltas.stripeMonthly }}</span
                                                >
                                            }
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else {
                                            <ion-input
                                                type="number"
                                                formControlName="stripeMonthly"
                                                placeholder="Enter manually"
                                            ></ion-input>
                                            <ion-badge color="warning">manual</ion-badge>
                                        }
                                    </div>
                                </ion-item>
                                <ion-item>
                                    <ion-label>Stripe Annual</ion-label>
                                    <div slot="end" class="auto-value">
                                        @if (stripeLoading) {
                                            <ion-spinner name="dots"></ion-spinner>
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else if (preview?.stripeAnnual != null) {
                                            <span>{{ preview?.stripeAnnual }}</span>
                                            @if (previewDeltas.stripeAnnual !== null && previewDeltas.stripeAnnual !== 0) {
                                                <span [class]="previewDeltas.stripeAnnual > 0 ? 'delta-up' : 'delta-down'"
                                                    >{{ previewDeltas.stripeAnnual > 0 ? '↑ +' : '↓ '
                                                    }}{{ previewDeltas.stripeAnnual }}</span
                                                >
                                            }
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else {
                                            <ion-input
                                                type="number"
                                                formControlName="stripeAnnual"
                                                placeholder="Enter manually"
                                            ></ion-input>
                                            <ion-badge color="warning">manual</ion-badge>
                                        }
                                    </div>
                                </ion-item>
                            }
                            @if (capabilities?.appStore) {
                                <ion-item>
                                    <ion-label>App Store</ion-label>
                                    <div slot="end" class="auto-value">
                                        @if (appStoreLoading) {
                                            <ion-spinner name="dots"></ion-spinner>
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else if (preview?.appStore != null) {
                                            <span>{{ preview?.appStore }}</span>
                                            @if (previewDeltas.appStore !== null && previewDeltas.appStore !== 0) {
                                                <span [class]="previewDeltas.appStore > 0 ? 'delta-up' : 'delta-down'"
                                                    >{{ previewDeltas.appStore > 0 ? '↑ +' : '↓ ' }}{{ previewDeltas.appStore }}</span
                                                >
                                            }
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else {
                                            <ion-input type="number" formControlName="appStore" placeholder="Enter manually"></ion-input>
                                            <ion-badge color="warning">manual</ion-badge>
                                        }
                                    </div>
                                </ion-item>
                            } @else {
                                <ion-item>
                                    <ion-label>App Store</ion-label>
                                    <ion-input type="number" formControlName="appStore" slot="end" placeholder="—"></ion-input>
                                </ion-item>
                            }
                            @if (capabilities?.googlePlay) {
                                <ion-item>
                                    <ion-label>Google Play</ion-label>
                                    <div slot="end" class="auto-value">
                                        @if (googlePlayLoading) {
                                            <ion-spinner name="dots"></ion-spinner>
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else if (preview?.googlePlay != null) {
                                            <span>{{ preview?.googlePlay }}</span>
                                            @if (previewDeltas.googlePlay !== null && previewDeltas.googlePlay !== 0) {
                                                <span [class]="previewDeltas.googlePlay > 0 ? 'delta-up' : 'delta-down'"
                                                    >{{ previewDeltas.googlePlay > 0 ? '↑ +' : '↓ ' }}{{ previewDeltas.googlePlay }}</span
                                                >
                                            }
                                            <ion-badge color="primary">auto</ion-badge>
                                        } @else {
                                            <ion-input type="number" formControlName="googlePlay" placeholder="Enter manually"></ion-input>
                                            <ion-badge color="warning">manual</ion-badge>
                                        }
                                    </div>
                                </ion-item>
                            } @else {
                                <ion-item>
                                    <ion-label>Google Play</ion-label>
                                    <ion-input type="number" formControlName="googlePlay" slot="end" placeholder="—"></ion-input>
                                </ion-item>
                            }
                        </ion-list>
                        <div class="buttons">
                            <ion-button (click)="takeSnapshot()" [disabled]="snapshotInProgress">
                                @if (snapshotInProgress) {
                                    <ion-spinner slot="start" name="crescent"></ion-spinner>
                                }
                                Take Snapshot
                            </ion-button>
                        </div>
                        @if (latestSnapshot) {
                            <p class="snapshot-meta">Last snapshot: {{ latestSnapshot.createdAt | date: 'medium' }}</p>
                        }
                        @if (schedulerStatus) {
                            @if (schedulerStatus.enabled) {
                                <p class="snapshot-meta">
                                    Next automatic snapshot:
                                    @if (schedulerStatus.nextRunAt) {
                                        {{ schedulerStatus.nextRunAt | date: 'medium' }}
                                    } @else {
                                        pending
                                    }
                                    @if (schedulerCountdownLabel) {
                                        <span> (in {{ schedulerCountdownLabel }})</span>
                                    }
                                </p>
                            } @else {
                                <p class="snapshot-meta">Automatic weekly snapshots are currently disabled.</p>
                            }
                        }
                    </div>
                }

                <!-- SNAPSHOT HISTORY -->
                @if (snapshots.length > 0) {
                    <div class="analytics-section">
                        <h2 class="section-title">Snapshot History</h2>
                        <ion-list>
                            @for (snap of recentSnapshots; track snap._id) {
                                <ion-item>
                                    <ion-label>
                                        <p class="snapshot-date">{{ snap.createdAt | date: 'medium' }}</p>
                                        <p class="snapshot-summary">
                                            @if (
                                                snap.subscribers?.stripeMonthly != null ||
                                                snap.subscribers?.stripeAnnual != null ||
                                                snap.subscribers?.appStore != null ||
                                                snap.subscribers?.googlePlay != null
                                            ) {
                                                Subscribers:
                                                {{
                                                    (snap.subscribers?.stripeMonthly ?? 0) +
                                                        (snap.subscribers?.stripeAnnual ?? 0) +
                                                        (snap.subscribers?.appStore ?? 0) +
                                                        (snap.subscribers?.googlePlay ?? 0)
                                                }}
                                            }
                                            @if (snap.revenue?.totalNetMrr != null) {
                                                &nbsp;·&nbsp;MRR: £{{ snap.revenue?.totalNetMrr | number: '1.0-0' }}
                                            }
                                        </p>
                                    </ion-label>
                                    @if (canEdit) {
                                        <ion-button
                                            slot="end"
                                            fill="clear"
                                            color="danger"
                                            (click)="deleteSnapshot(snap._id!)"
                                            [disabled]="deletingSnapshotId === snap._id"
                                        >
                                            @if (deletingSnapshotId === snap._id) {
                                                <ion-spinner name="crescent" slot="icon-only"></ion-spinner>
                                            } @else {
                                                <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
                                            }
                                        </ion-button>
                                    }
                                </ion-item>
                            }
                        </ion-list>
                    </div>
                }

                <!-- ACTIVE USERS CHART -->
                <div class="analytics-section">
                    <h2 class="section-title">Active Users</h2>
                    @if (latestSnapshot) {
                        @if (capabilities?.vouchers && latestSnapshot.vouchers != null) {
                            <ion-list>
                                <ion-item>
                                    <ion-label>Vouchers Generated</ion-label>
                                    <ion-note slot="end">{{ latestSnapshot.vouchers }}</ion-note>
                                    @if (latestDeltas.vouchers !== null && latestDeltas.vouchers !== 0) {
                                        <span slot="end" [class]="latestDeltas.vouchers > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.vouchers > 0 ? '↑ +' : '↓ ' }}{{ latestDeltas.vouchers }}</span
                                        >
                                    }
                                </ion-item>
                            </ion-list>
                        }
                    }
                    <div style="position: relative; height: 300px; width: 100%">
                        <canvas #barCanvas></canvas>
                    </div>
                    <div class="active-users-filter" [formGroup]="form">
                        <div class="active-users-filter__controls">
                            <div class="active-users-filter__group">
                                <span class="active-users-filter__group-label">Date Range</span>
                                <div class="active-users-filter__preset-buttons">
                                    @for (preset of rangePresets; track preset.value) {
                                        <ion-button
                                            class="btn-sm"
                                            fill="outline"
                                            [color]="form.controls.period.value === preset.value ? 'primary' : 'medium'"
                                            (click)="onRangePresetClick(preset.value)"
                                        >
                                            <span>{{ preset.label }}</span>
                                        </ion-button>
                                    }
                                </div>
                            </div>
                            <div class="active-users-filter__group">
                                <span class="active-users-filter__group-label">Group By</span>
                                <ion-segment formControlName="unit" (ionChange)="onUnitChanged($event.detail.value || 'week')">
                                    <ion-segment-button value="day">
                                        <ion-label>Days</ion-label>
                                    </ion-segment-button>
                                    <ion-segment-button value="week">
                                        <ion-label>Weeks</ion-label>
                                    </ion-segment-button>
                                    <ion-segment-button value="month">
                                        <ion-label>Months</ion-label>
                                    </ion-segment-button>
                                </ion-segment>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- SUBSCRIBERS OVER TIME -->
                <div class="analytics-section">
                    <h2 class="section-title">Subscribers Over Time</h2>
                    @if (latestSnapshot) {
                        <ion-list>
                            @if (capabilities?.stripe && latestSnapshot.subscribers?.stripeMonthly != null) {
                                <ion-item>
                                    <ion-label>Stripe Monthly</ion-label>
                                    <ion-note slot="end">{{ latestSnapshot.subscribers?.stripeMonthly }}</ion-note>
                                    @if (latestDeltas.stripeMonthly !== null && latestDeltas.stripeMonthly !== 0) {
                                        <span slot="end" [class]="latestDeltas.stripeMonthly > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.stripeMonthly > 0 ? '↑ +' : '↓ ' }}{{ latestDeltas.stripeMonthly }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (capabilities?.stripe && latestSnapshot.subscribers?.stripeAnnual != null) {
                                <ion-item>
                                    <ion-label>Stripe Annual</ion-label>
                                    <ion-note slot="end">{{ latestSnapshot.subscribers?.stripeAnnual }}</ion-note>
                                    @if (latestDeltas.stripeAnnual !== null && latestDeltas.stripeAnnual !== 0) {
                                        <span slot="end" [class]="latestDeltas.stripeAnnual > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.stripeAnnual > 0 ? '↑ +' : '↓ ' }}{{ latestDeltas.stripeAnnual }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (latestSnapshot.subscribers?.appStore != null) {
                                <ion-item>
                                    <ion-label>App Store</ion-label>
                                    <ion-note slot="end">{{ latestSnapshot.subscribers?.appStore }}</ion-note>
                                    @if (latestDeltas.appStore !== null && latestDeltas.appStore !== 0) {
                                        <span slot="end" [class]="latestDeltas.appStore > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.appStore > 0 ? '↑ +' : '↓ ' }}{{ latestDeltas.appStore }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (latestSnapshot.subscribers?.googlePlay != null) {
                                <ion-item>
                                    <ion-label>Google Play</ion-label>
                                    <ion-note slot="end">{{ latestSnapshot.subscribers?.googlePlay }}</ion-note>
                                    @if (latestDeltas.googlePlay !== null && latestDeltas.googlePlay !== 0) {
                                        <span slot="end" [class]="latestDeltas.googlePlay > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.googlePlay > 0 ? '↑ +' : '↓ ' }}{{ latestDeltas.googlePlay }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (latestSubscriberTotal != null) {
                                <ion-item>
                                    <ion-label><strong>Total</strong></ion-label>
                                    <ion-note slot="end"
                                        ><strong>{{ latestSubscriberTotal }}</strong></ion-note
                                    >
                                    @if (latestDeltas.subscribersTotal !== null && latestDeltas.subscribersTotal !== 0) {
                                        <span slot="end" [class]="latestDeltas.subscribersTotal > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.subscribersTotal > 0 ? '↑ +' : '↓ ' }}{{ latestDeltas.subscribersTotal }}</span
                                        >
                                    }
                                </ion-item>
                            }
                        </ion-list>
                    }
                    @if (snapshots.length > 1) {
                        <div style="position: relative; height: 300px; width: 100%">
                            <canvas #subscribersCanvas></canvas>
                        </div>
                    } @else {
                        <p class="chart-placeholder">Take at least 2 snapshots to see subscriber trends.</p>
                    }
                </div>

                <!-- FORCES OVER TIME -->
                <div class="analytics-section">
                    <h2 class="section-title">Forces Per Game Over Time</h2>
                    @if (latestSnapshot) {
                        @if (latestSnapshot.forces?.length) {
                            <ion-list>
                                @for (item of latestSnapshot.forces; track item.gameId) {
                                    <ion-item>
                                        <ion-label>{{ item.gameId }}</ion-label>
                                        <ion-note slot="end">{{ item.count }}</ion-note>
                                    </ion-item>
                                }
                            </ion-list>
                        }
                    }
                    @if (snapshots.length > 1) {
                        <div style="position: relative; height: 300px; width: 100%">
                            <canvas #forcesCanvas></canvas>
                        </div>
                    } @else {
                        <p class="chart-placeholder">Take at least 2 snapshots to see force trends.</p>
                    }
                </div>

                <!-- MONTHLY RECURRING REVENUE -->
                <div class="analytics-section">
                    <h2 class="section-title">Monthly Recurring Revenue</h2>
                    @if (latestSnapshot) {
                        <ion-list>
                            @if (capabilities?.stripe && latestSnapshot.revenue?.stripeNetMrr != null) {
                                <ion-item>
                                    <ion-label>Stripe Net MRR</ion-label>
                                    <ion-note slot="end">£{{ latestSnapshot.revenue?.stripeNetMrr | number: '1.2-2' }}</ion-note>
                                    @if (latestDeltas.stripeNetMrr !== null && latestDeltas.stripeNetMrr !== 0) {
                                        <span slot="end" [class]="latestDeltas.stripeNetMrr > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.stripeNetMrr > 0 ? '↑ +' : '↓ ' }}£{{
                                                latestDeltas.stripeNetMrr | number: '1.2-2'
                                            }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (capabilities?.stripe && latestSnapshot.revenue?.stripeGrossMrr != null) {
                                <ion-item>
                                    <ion-label>Stripe Gross MRR</ion-label>
                                    <ion-note slot="end">£{{ latestSnapshot.revenue?.stripeGrossMrr | number: '1.2-2' }}</ion-note>
                                    @if (latestDeltas.stripeGrossMrr !== null && latestDeltas.stripeGrossMrr !== 0) {
                                        <span slot="end" [class]="latestDeltas.stripeGrossMrr > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.stripeGrossMrr > 0 ? '↑ +' : '↓ ' }}£{{
                                                latestDeltas.stripeGrossMrr | number: '1.2-2'
                                            }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (latestSnapshot.revenue?.appStoreNet != null) {
                                <ion-item>
                                    <ion-label>App Store Net MRR</ion-label>
                                    <ion-note slot="end">£{{ latestSnapshot.revenue?.appStoreNet | number: '1.2-2' }}</ion-note>
                                    @if (latestDeltas.appStoreNet !== null && latestDeltas.appStoreNet !== 0) {
                                        <span slot="end" [class]="latestDeltas.appStoreNet > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.appStoreNet > 0 ? '↑ +' : '↓ ' }}£{{
                                                latestDeltas.appStoreNet | number: '1.2-2'
                                            }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (latestSnapshot.revenue?.googlePlayNet != null) {
                                <ion-item>
                                    <ion-label>Google Play Net MRR</ion-label>
                                    <ion-note slot="end">£{{ latestSnapshot.revenue?.googlePlayNet | number: '1.2-2' }}</ion-note>
                                    @if (latestDeltas.googlePlayNet !== null && latestDeltas.googlePlayNet !== 0) {
                                        <span slot="end" [class]="latestDeltas.googlePlayNet > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.googlePlayNet > 0 ? '↑ +' : '↓ ' }}£{{
                                                latestDeltas.googlePlayNet | number: '1.2-2'
                                            }}</span
                                        >
                                    }
                                </ion-item>
                            }
                            @if (latestSnapshot.revenue?.totalNetMrr != null) {
                                <ion-item>
                                    <ion-label>Total Net MRR</ion-label>
                                    <ion-note slot="end">£{{ latestSnapshot.revenue?.totalNetMrr | number: '1.2-2' }}</ion-note>
                                    @if (latestDeltas.totalNetMrr !== null && latestDeltas.totalNetMrr !== 0) {
                                        <span slot="end" [class]="latestDeltas.totalNetMrr > 0 ? 'delta-up' : 'delta-down'"
                                            >{{ latestDeltas.totalNetMrr > 0 ? '↑ +' : '↓ ' }}£{{
                                                latestDeltas.totalNetMrr | number: '1.2-2'
                                            }}</span
                                        >
                                    }
                                </ion-item>
                            }
                        </ion-list>
                    }
                    @if (snapshots.length > 1) {
                        <div style="position: relative; height: 300px; width: 100%">
                            <canvas #revenueCanvas></canvas>
                        </div>
                    } @else {
                        <p class="chart-placeholder">Take at least 2 snapshots to see revenue trends.</p>
                    }
                </div>

                <!-- MRR DELTA -->
                <div class="analytics-section">
                    <h2 class="section-title">MRR Delta</h2>
                    @if (snapshots.length > 1) {
                        <div style="position: relative; height: 300px; width: 100%">
                            <canvas #mrrDeltaCanvas></canvas>
                        </div>
                    } @else {
                        <p class="chart-placeholder">Take at least 2 snapshots to see MRR changes.</p>
                    }
                </div>
            </div>
        </abs-page-layout>
    `,
    styles: [
        `
            :host {
                --wrapWidth: 100%;
                max-width: 100%;
                margin: 0 auto;
                display: block;
            }

            .dashboard-grid {
                display: grid;
                grid-template-columns: 1fr;
                gap: 16px;
                padding: 16px;
            }
            @media (min-width: 900px) {
                .dashboard-grid {
                    grid-template-columns: repeat(2, 1fr);
                }
            }
            @media (min-width: 1400px) {
                .dashboard-grid {
                    grid-template-columns: repeat(3, 1fr);
                }
            }
            .dashboard-grid .analytics-section {
                margin: 0;
                display: flex;
                flex-direction: column;
                height: 100%;
            }
            .dashboard-grid .analytics-section > div[style*='position: relative'] {
                flex-grow: 1;
                min-height: 300px;
            }
            @media (min-width: 992px) {
                .dashboard-grid .analytics-section > div[style*='position: relative'] {
                    height: 450px !important;
                    min-height: 450px;
                }
            }
            .analytics-section {
                margin: 16px 0;
                padding: 16px;
                border-radius: 8px;
                background: var(--ion-color-light, #f4f5f8);
            }
            .analytics-section--placeholder {
                opacity: 0.6;
            }
            .section-title {
                margin: 0 0 12px;
                font-size: 1.1rem;
                font-weight: 600;
            }
            .chart-placeholder {
                color: var(--ion-color-medium, #92949c);
                font-style: italic;
                text-align: center;
                padding: 24px 0;
                margin: 0;
            }
            .snapshot-meta {
                color: var(--ion-color-medium, #92949c);
                font-size: 0.85rem;
                margin: 4px 0 12px;
            }
            .auto-value {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 1rem;
            }
            .delta-up {
                font-size: 0.8rem;
                font-weight: 600;
                color: var(--ion-color-success, #2dd36f);
            }
            .delta-down {
                font-size: 0.8rem;
                font-weight: 600;
                color: var(--ion-color-danger, #eb445a);
            }
            .snapshot-date {
                font-size: 0.9rem;
                font-weight: 500;
                margin-bottom: 2px;
            }
            .snapshot-summary {
                font-size: 0.8rem;
                color: var(--ion-color-medium, #92949c);
            }
            .active-users-filter {
                margin-top: 12px;
                display: grid;
                gap: 10px;
            }
            .active-users-filter__controls {
                display: grid;
                gap: 10px;
                grid-template-columns: 1fr;
            }
            .active-users-filter__group {
                display: grid;
                gap: 4px;
            }
            .active-users-filter__preset-buttons {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
            }
            .active-users-filter__group-label {
                color: var(--ion-color-medium, #92949c);
                font-size: 0.8rem;
                font-weight: 500;
            }
            @media (min-width: 680px) {
                .active-users-filter__controls {
                    grid-template-columns: 1fr 1fr;
                    align-items: start;
                }
            }
        `
    ]
})
export class AnalyticsPage implements OnDestroy, AfterViewInit {
    @ViewChild('barCanvas') private barCanvas!: ElementRef;
    @ViewChild('subscribersCanvas') private subscribersCanvas?: ElementRef;
    @ViewChild('forcesCanvas') private forcesCanvas?: ElementRef;
    @ViewChild('revenueCanvas') private revenueCanvas?: ElementRef;
    @ViewChild('mrrDeltaCanvas') private mrrDeltaCanvas?: ElementRef;

    protected form: FormGroup;
    protected snapshotForm: FormGroup;

    snapshots: AnalyticsSnapshot[] = [];
    latestSnapshot: AnalyticsSnapshot | null = null;
    snapshotInProgress = false;
    deletingSnapshotId: string | null = null;
    capabilities: AnalyticsCapabilities | null = null;
    get canEdit(): boolean {
        return this.userService.user()?.roles?.includes('app_admin') ?? false;
    }
    preview: AnalyticsPreview | null = null;
    stripeLoading = false;
    appStoreLoading = false;
    googlePlayLoading = false;
    previewDeltas: { stripeMonthly: number | null; stripeAnnual: number | null; appStore: number | null; googlePlay: number | null } = {
        stripeMonthly: null,
        stripeAnnual: null,
        appStore: null,
        googlePlay: null
    };
    latestDeltas: {
        stripeMonthly: number | null;
        stripeAnnual: number | null;
        appStore: number | null;
        googlePlay: number | null;
        subscribersTotal: number | null;
        vouchers: number | null;
        stripeNetMrr: number | null;
        stripeGrossMrr: number | null;
        appStoreNet: number | null;
        googlePlayNet: number | null;
        totalNetMrr: number | null;
    } = {
        stripeMonthly: null,
        stripeAnnual: null,
        appStore: null,
        googlePlay: null,
        subscribersTotal: null,
        vouchers: null,
        stripeNetMrr: null,
        stripeGrossMrr: null,
        appStoreNet: null,
        googlePlayNet: null,
        totalNetMrr: null
    };

    private barChart: Chart | null = null;
    private subscribersChart: Chart | null = null;
    private forcesChart: Chart | null = null;
    private revenueChart: Chart | null = null;
    private mrrDeltaChart: Chart | null = null;
    private timer: any;
    private schedulerCountdownTimer: any;
    schedulerStatus: AnalyticsSchedulerStatus | null = null;
    schedulerCountdownSeconds: number | null = null;

    private readonly rangePresetMap: Record<string, { value: number; label: string }[]> = {
        day: [
            { value: 7, label: '7D' },
            { value: 14, label: '14D' },
            { value: 28, label: '28D' },
            { value: 180, label: '180D' },
            { value: 365, label: '365D' }
        ],
        week: [
            { value: 4, label: '4W' },
            { value: 8, label: '8W' },
            { value: 12, label: '12W' },
            { value: 26, label: '26W' },
            { value: 52, label: '52W' }
        ],
        month: [
            { value: 3, label: '3M' },
            { value: 6, label: '6M' },
            { value: 12, label: '12M' },
            { value: 18, label: '18M' },
            { value: 24, label: '24M' }
        ]
    };

    constructor(
        private userService: UserService,
        private toast: ToastService,
        private formBuilder: FormBuilder
    ) {
        this.form = this.formBuilder.group({
            period: this.formBuilder.control(52),
            unit: this.formBuilder.control('week')
        });
        this.snapshotForm = this.formBuilder.group({
            stripeMonthly: this.formBuilder.control<number | null>(null),
            stripeAnnual: this.formBuilder.control<number | null>(null),
            appStore: this.formBuilder.control<number | null>(null),
            googlePlay: this.formBuilder.control<number | null>(null)
        });

        this.regenerateBarchart();
        this.form.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.regenerateBarchart();
        });

        this.loadSnapshots();
        this.loadCapabilities();
        this.loadSchedulerStatus();
    }

    ngAfterViewInit(): void {}

    get schedulerCountdownLabel(): string {
        if (this.schedulerCountdownSeconds == null) return '';

        const seconds = Math.max(0, this.schedulerCountdownSeconds);
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    }

    async loadSchedulerStatus() {
        try {
            this.schedulerStatus = await this.userService.getAnalyticsSchedulerStatus();
            this.schedulerCountdownSeconds = this.schedulerStatus.nextRunInSeconds;
            this.startSchedulerCountdown();
        } catch (e) {
            console.error('Failed to load analytics scheduler status', e);
        }
    }

    private startSchedulerCountdown() {
        if (this.schedulerCountdownTimer) {
            clearInterval(this.schedulerCountdownTimer);
            this.schedulerCountdownTimer = null;
        }

        if (this.schedulerCountdownSeconds == null) return;

        this.schedulerCountdownTimer = setInterval(() => {
            if (this.schedulerCountdownSeconds == null) return;

            if (this.schedulerCountdownSeconds <= 0) {
                clearInterval(this.schedulerCountdownTimer);
                this.schedulerCountdownTimer = null;
                void this.loadSchedulerStatus();
                return;
            }

            this.schedulerCountdownSeconds -= 1;
        }, 1000);
    }

    get latestSubscriberTotal(): number | null {
        const s = this.latestSnapshot?.subscribers;
        if (!s) return null;
        const vals = [s.stripeMonthly, s.stripeAnnual, s.appStore, s.googlePlay].filter((v): v is number => v != null);
        return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) : null;
    }

    get rangePresets(): { value: number; label: string }[] {
        return this.rangePresetMap[this.form.controls.unit.value ?? 'week'] ?? this.rangePresetMap['week'];
    }

    onRangePresetClick(value: number) {
        this.form.patchValue({ period: value });
    }

    onUnitChanged(unit: string | number | null | undefined) {
        const normalizedUnit = String(unit ?? 'week');
        const presets = this.rangePresetMap[normalizedUnit] ?? this.rangePresetMap['week'];
        const currentPeriod = this.form.controls.period.value;
        if (!presets.some((preset) => preset.value === currentPeriod)) {
            this.form.patchValue({ period: presets[0].value });
        }
    }

    async loadCapabilities() {
        try {
            this.capabilities = await this.userService.getAnalyticsCapabilities();
            if (this.canEdit) {
                if (this.capabilities.stripe) {
                    this.stripeLoading = true;
                    this.userService
                        .getStripePreview()
                        .then((data) => {
                            this.preview = { ...this.preview, ...data };
                            this.updatePreviewDeltas();
                        })
                        .catch((e) => console.error('Failed to load Stripe preview', e))
                        .finally(() => (this.stripeLoading = false));
                }
                if (this.capabilities.appStore) {
                    this.appStoreLoading = true;
                    this.userService
                        .getAppStorePreview()
                        .then((data) => {
                            this.preview = { ...this.preview, ...data };
                            this.updatePreviewDeltas();
                        })
                        .catch((e) => console.error('Failed to load App Store preview', e))
                        .finally(() => (this.appStoreLoading = false));
                }
                if (this.capabilities.googlePlay) {
                    this.googlePlayLoading = true;
                    this.userService
                        .getGooglePlayPreview()
                        .then((data) => {
                            this.preview = { ...this.preview, ...data };
                            this.updatePreviewDeltas();
                        })
                        .catch((e) => console.error('Failed to load Google Play preview', e))
                        .finally(() => (this.googlePlayLoading = false));
                }
            }
        } catch (e) {
            console.error('Failed to load analytics capabilities', e);
        }
    }

    async loadSnapshots() {
        try {
            this.snapshots = await this.userService.getAnalyticsSnapshots();
            if (this.snapshots.length > 0) {
                this.latestSnapshot = this.snapshots[this.snapshots.length - 1];
                this.updatePreviewDeltas();
                this.updateLatestDeltas();
            }
            if (this.snapshots.length > 1) {
                setTimeout(() => this.buildSnapshotCharts(), 0);
            }
        } catch (e) {
            console.error('Failed to load analytics snapshots', e);
        }
    }

    async takeSnapshot() {
        if (!window.confirm('Take a new snapshot and archive active users for this period?')) {
            return;
        }
        this.snapshotInProgress = true;
        try {
            const { stripeMonthly, stripeAnnual, appStore, googlePlay } = this.snapshotForm.value;
            const body: Partial<AnalyticsSnapshotDto> = { archiveActiveUsers: true };
            // Use form values first; fall back to already-loaded preview so Stripe isn't re-fetched
            if (stripeMonthly != null) body['stripeMonthly'] = stripeMonthly;
            else if (this.preview?.stripeMonthly != null) body['stripeMonthly'] = this.preview.stripeMonthly;
            if (stripeAnnual != null) body['stripeAnnual'] = stripeAnnual;
            else if (this.preview?.stripeAnnual != null) body['stripeAnnual'] = this.preview.stripeAnnual;
            if (appStore != null) body['appStore'] = appStore;
            else if (this.preview?.appStore != null) body['appStore'] = this.preview.appStore;
            if (googlePlay != null) body['googlePlay'] = googlePlay;
            else if (this.preview?.googlePlay != null) body['googlePlay'] = this.preview.googlePlay;

            const newSnapshot = await this.userService.takeAnalyticsSnapshot(body);
            this.snapshots = [...this.snapshots, newSnapshot];
            this.latestSnapshot = newSnapshot;
            this.updateLatestDeltas();
            this.snapshotForm.reset();

            if (this.snapshots.length > 1) {
                setTimeout(() => this.buildSnapshotCharts(), 0);
            }
            this.toast.showToast('Success', 'Snapshot recorded', { color: 'success', duration: 3000 });
        } catch {
            this.toast.showToast('Error', 'Failed to take snapshot', { color: 'danger', duration: 3000 });
        } finally {
            this.snapshotInProgress = false;
        }
    }

    get recentSnapshots(): AnalyticsSnapshot[] {
        return this.snapshots.slice(-5).reverse();
    }

    async deleteSnapshot(id: string) {
        this.deletingSnapshotId = id;
        try {
            await this.userService.deleteAnalyticsSnapshot(id);
            this.snapshots = this.snapshots.filter((s) => s._id !== id);
            this.latestSnapshot = this.snapshots.length > 0 ? this.snapshots[this.snapshots.length - 1] : null;
            this.updateLatestDeltas();
            if (this.snapshots.length > 1) {
                setTimeout(() => this.buildSnapshotCharts(), 0);
            } else {
                this.subscribersChart?.destroy();
                this.subscribersChart = null;
                this.forcesChart?.destroy();
                this.forcesChart = null;
                this.revenueChart?.destroy();
                this.revenueChart = null;
                this.mrrDeltaChart?.destroy();
                this.mrrDeltaChart = null;
            }
            this.toast.showToast('Deleted', 'Snapshot removed', { color: 'success', duration: 2000 });
        } catch {
            this.toast.showToast('Error', 'Failed to delete snapshot', { color: 'danger', duration: 3000 });
        } finally {
            this.deletingSnapshotId = null;
        }
    }

    private updateLatestDeltas() {
        const nullDeltas = {
            stripeMonthly: null,
            stripeAnnual: null,
            appStore: null,
            googlePlay: null,
            subscribersTotal: null,
            vouchers: null,
            stripeNetMrr: null,
            stripeGrossMrr: null,
            appStoreNet: null,
            googlePlayNet: null,
            totalNetMrr: null
        };
        if (this.snapshots.length < 2) {
            this.latestDeltas = nullDeltas;
            return;
        }
        const curr = this.snapshots[this.snapshots.length - 1];
        const prev = this.snapshots[this.snapshots.length - 2];
        const d = (c: number | undefined, p: number | undefined): number | null => (c != null && p != null ? c - p : null);
        const sumSubs = (snap: AnalyticsSnapshot) => {
            const s = snap.subscribers;
            if (!s) return null;
            const vals = [s.stripeMonthly, s.stripeAnnual, s.appStore, s.googlePlay].filter((v): v is number => v != null);
            return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) : null;
        };
        const currTotal = sumSubs(curr);
        const prevTotal = sumSubs(prev);
        this.latestDeltas = {
            stripeMonthly: d(curr.subscribers?.stripeMonthly, prev.subscribers?.stripeMonthly),
            stripeAnnual: d(curr.subscribers?.stripeAnnual, prev.subscribers?.stripeAnnual),
            appStore: d(curr.subscribers?.appStore, prev.subscribers?.appStore),
            googlePlay: d(curr.subscribers?.googlePlay, prev.subscribers?.googlePlay),
            subscribersTotal: d(currTotal ?? undefined, prevTotal ?? undefined),
            vouchers: d(curr.vouchers, prev.vouchers),
            stripeNetMrr: d(curr.revenue?.stripeNetMrr, prev.revenue?.stripeNetMrr),
            stripeGrossMrr: d(curr.revenue?.stripeGrossMrr, prev.revenue?.stripeGrossMrr),
            appStoreNet: d(curr.revenue?.appStoreNet, prev.revenue?.appStoreNet),
            googlePlayNet: d(curr.revenue?.googlePlayNet, prev.revenue?.googlePlayNet),
            totalNetMrr: d(curr.revenue?.totalNetMrr, prev.revenue?.totalNetMrr)
        };
    }

    async regenerateBarchart() {
        if (this.timer) clearInterval(this.timer);
        this.timer = setInterval(() => this.updateData(), 60000);
        this.updateData();
    }

    async updateData() {
        const stats: any = await this.userService.getStats(this.form.value.unit, this.form.value.period);
        if (this.barChart) {
            this.barChart.data.labels = stats.labels;
            this.barChart.data.datasets[0].data = stats.values;
            this.barChart.update();
        } else {
            this.createBarChart(stats);
        }
    }

    private createBarChart(stats: { labels: string[]; values: number[] }) {
        if (!this.barCanvas) return;
        this.barChart = new Chart(this.barCanvas.nativeElement, {
            type: 'bar',
            data: {
                labels: stats.labels,
                datasets: [
                    {
                        type: 'bar',
                        label: '# of Active Users',
                        data: stats.values,
                        borderWidth: 0,
                        backgroundColor: '#006',
                        yAxisID: 'userAxis'
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    userAxis: {
                        position: 'left',
                        suggestedMax: 10
                    },
                    x: {
                        ticks: {
                            callback: function (value: string | number) {
                                return this.getLabelForValue(+value);
                            }
                        }
                    }
                }
            }
        });
    }

    private updatePreviewDeltas() {
        const p = this.preview;
        const s = this.latestSnapshot?.subscribers;
        const d = (curr: number | undefined, prev: number | undefined): number | null =>
            curr != null && prev != null ? curr - prev : null;
        this.previewDeltas = {
            stripeMonthly: d(p?.stripeMonthly, s?.stripeMonthly),
            stripeAnnual: d(p?.stripeAnnual, s?.stripeAnnual),
            appStore: d(p?.appStore, s?.appStore),
            googlePlay: d(p?.googlePlay, s?.googlePlay)
        };
    }

    private buildSnapshotCharts() {
        this.buildSubscribersChart();
        this.buildForcesChart();
        this.buildRevenueChart();
        this.buildMrrDeltaChart();
    }

    private buildSubscribersChart() {
        if (!this.subscribersCanvas) return;

        const labels = this.snapshots.map((s) => new Date(s.createdAt).toLocaleDateString());
        const platforms: { key: keyof NonNullable<AnalyticsSnapshot['subscribers']>; label: string; color: string }[] = [
            { key: 'stripeMonthly', label: 'Stripe Monthly', color: CHART_COLORS[0] },
            { key: 'stripeAnnual', label: 'Stripe Annual', color: CHART_COLORS[1] },
            { key: 'appStore', label: 'App Store', color: CHART_COLORS[2] },
            { key: 'googlePlay', label: 'Google Play', color: CHART_COLORS[3] }
        ];

        const datasets = platforms
            .filter((p) => this.snapshots.some((s) => s.subscribers?.[p.key] != null))
            .map((p) => ({
                label: p.label,
                data: this.snapshots.map((s) => s.subscribers?.[p.key] ?? null),
                borderColor: p.color,
                backgroundColor: p.color,
                tension: 0.3,
                spanGaps: true,
                pointRadius: 0
            }));

        const totalData = this.snapshots.map((s) => {
            const subs = s.subscribers;
            if (!subs) return null;
            const vals = [subs.stripeMonthly, subs.stripeAnnual, subs.appStore, subs.googlePlay].filter((v): v is number => v != null);
            return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) : null;
        });
        if (totalData.some((v) => v != null)) {
            datasets.push({
                label: 'Total',
                data: totalData,
                borderColor: CHART_COLORS[4],
                backgroundColor: CHART_COLORS[4],
                tension: 0.3,
                spanGaps: true,
                pointRadius: 0
            });
        }

        if (this.subscribersChart) this.subscribersChart.destroy();
        this.subscribersChart = new Chart(this.subscribersCanvas.nativeElement, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false }
            }
        });
    }

    private buildForcesChart() {
        if (!this.forcesCanvas) return;

        const labels = this.snapshots.map((s) => new Date(s.createdAt).toLocaleDateString());
        const gameIds = [...new Set(this.snapshots.flatMap((s) => (s.forces ?? []).map((f) => f.gameId)))].sort();

        const datasets = gameIds.map((gameId, i) => ({
            label: gameId,
            data: this.snapshots.map((s) => s.forces?.find((f) => f.gameId === gameId)?.count ?? null),
            borderColor: CHART_COLORS[i % CHART_COLORS.length],
            backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
            tension: 0.3,
            spanGaps: true,
            pointRadius: 0
        }));

        if (this.forcesChart) this.forcesChart.destroy();
        this.forcesChart = new Chart(this.forcesCanvas.nativeElement, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false }
            }
        });
    }

    private buildRevenueChart() {
        if (!this.revenueCanvas) return;

        const labels = this.snapshots.map((s) => new Date(s.createdAt).toLocaleDateString());
        type RevenueKey = keyof NonNullable<SnapshotRevenue>;
        const platforms: { key: RevenueKey; label: string; color: string }[] = [
            { key: 'appStoreNet' as RevenueKey, label: 'Net (iOS)*', color: 'rgba(255, 193,  7, 0.85)' },
            { key: 'googlePlayNet' as RevenueKey, label: 'Net (Android)', color: 'rgba(220,  53, 69, 0.85)' },
            { key: 'stripeNetMrr' as RevenueKey, label: 'Net (Web)', color: 'rgba( 13, 110,253, 0.85)' }
        ].filter((p) => this.snapshots.some((s) => s.revenue?.[p.key] != null));

        const datasets = platforms.map((p) => ({
            label: p.label,
            data: this.snapshots.map((s) => s.revenue?.[p.key] ?? 0),
            backgroundColor: p.color,
            borderColor: p.color.replace('0.85', '1'),
            borderWidth: 1
        }));

        if (this.revenueChart) this.revenueChart.destroy();
        this.revenueChart = new Chart(this.revenueCanvas.nativeElement, {
            type: 'bar',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: £${(ctx.parsed.y as number).toFixed(2)}`,
                            footer: (items) => `Total: £${items.reduce((s, i) => s + (i.parsed.y as number), 0).toFixed(2)}`
                        }
                    }
                },
                scales: {
                    x: { stacked: true },
                    y: { stacked: true, ticks: { callback: (v) => `£${(+v).toFixed(0)}` } }
                }
            }
        });
    }

    private buildMrrDeltaChart() {
        if (!this.mrrDeltaCanvas || this.snapshots.length < 2) return;

        const labels = this.snapshots.slice(1).map((s) => new Date(s.createdAt).toLocaleDateString());
        type RevenueKey = keyof NonNullable<SnapshotRevenue>;
        const platforms: { key: RevenueKey; label: string; color: string }[] = [
            { key: 'appStoreNet' as RevenueKey, label: 'Net (iOS)*', color: 'rgba(255, 193,  7, 0.85)' },
            { key: 'googlePlayNet' as RevenueKey, label: 'Net (Android)', color: 'rgba(220,  53, 69, 0.85)' },
            { key: 'stripeNetMrr' as RevenueKey, label: 'Net (Web)', color: 'rgba( 13, 110,253, 0.85)' }
        ].filter((p) => this.snapshots.some((s) => s.revenue?.[p.key] != null));

        const datasets = platforms.map((p) => ({
            label: p.label,
            data: this.snapshots.slice(1).map((s, i) => {
                const prev = this.snapshots[i].revenue?.[p.key] ?? 0;
                const curr = s.revenue?.[p.key] ?? 0;
                return +(curr - prev).toFixed(2);
            }),
            backgroundColor: p.color,
            borderColor: p.color.replace('0.85', '1'),
            borderWidth: 1,
            stack: 'platform'
        }));

        if (this.mrrDeltaChart) this.mrrDeltaChart.destroy();
        this.mrrDeltaChart = new Chart(this.mrrDeltaCanvas.nativeElement, {
            type: 'bar',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: £${(ctx.parsed.y as number).toFixed(2)}`,
                            footer: (items) => `Total: £${items.reduce((s, i) => s + (i.parsed.y as number), 0).toFixed(2)}`
                        }
                    }
                },
                scales: {
                    x: { stacked: true },
                    y: { stacked: true, min: -400, ticks: { callback: (v) => `£${(+v).toFixed(0)}` } }
                }
            }
        });
    }

    ngOnDestroy(): void {
        if (this.timer) clearInterval(this.timer);
        if (this.schedulerCountdownTimer) clearInterval(this.schedulerCountdownTimer);
        this.barChart?.destroy();
        this.subscribersChart?.destroy();
        this.forcesChart?.destroy();
        this.revenueChart?.destroy();
        this.mrrDeltaChart?.destroy();
    }
}
